import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import type { HelpFAQ } from '@/content/help/types';

interface HelpFAQListProps {
  faqs: HelpFAQ[];
  searchQuery?: string;
}

function highlightText(text: string, query: string): React.ReactNode {
  if (!query.trim()) {
    return text;
  }

  const parts = text.split(new RegExp(`(${escapeRegExp(query)})`, 'gi'));

  return parts.map((part, index) =>
    part.toLowerCase() === query.toLowerCase() ? (
      <mark key={index} className="bg-yellow-200 dark:bg-yellow-800/40 rounded px-0.5">
        {part}
      </mark>
    ) : (
      part
    )
  );
}

function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function HelpFAQList({ faqs, searchQuery = '' }: HelpFAQListProps) {
  if (faqs.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic py-4">
        No FAQs available for this topic.
      </p>
    );
  }

  // Filter FAQs if there's a search query
  const filteredFaqs = searchQuery.trim()
    ? faqs.filter(
        (faq) =>
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.tags.some((tag) =>
            tag.toLowerCase().includes(searchQuery.toLowerCase())
          )
      )
    : faqs;

  if (filteredFaqs.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic py-4">
        No FAQs match your search.
      </p>
    );
  }

  return (
    <Accordion type="single" collapsible className="w-full">
      {filteredFaqs.map((faq) => (
        <AccordionItem key={faq.id} value={faq.id}>
          <AccordionTrigger className="text-left text-sm hover:no-underline">
            {highlightText(faq.question, searchQuery)}
          </AccordionTrigger>
          <AccordionContent className="text-sm text-muted-foreground">
            {highlightText(faq.answer, searchQuery)}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
